import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogActions, Button } from "@mui/material";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import {
  buscarVersoesdaPrevia,
  iniciarProjetoIcms,
  criarVersaoProjeto,
} from "../../../services/api-planejamento-tributario.service";
import { iniciarProjeto as iniciarProjetoPisCofins } from "../../../services/api-piscofins.service";
import SnackBar from "../../SnackBar";
import Loading from "../../Loading";
import styles from "./styles.module.css";

/**
 * ModalVersoesProjeto
 * - Lista versões da PRÉVIA e permite gerar um PROJETO com base em uma versão
 * - Envia apenas IDs para a API (idPrevia, idPreviaVersao, idProjeto)
 */
const ModalVersoesProjeto = ({
  open,
  onClose,
  tipoProjeto = "PIS-COFINS", // "PIS-COFINS" | "ICMS"
  idPrevia,
  rechargeData,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [versoes, setVersoes] = useState([]);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });

  const tipoParam = useMemo(() => {
    return tipoProjeto === "ICMS" ? "ICMS" : "PIS-COFINS";
  }, [tipoProjeto]);

  useEffect(() => {
    const carregarVersoes = async () => {
      if (!open || !idPrevia) {
        return;
      }

      try {
        setLoading(true);
        const resp = await buscarVersoesdaPrevia(tipoParam, idPrevia);
        const data = Array.isArray(resp) ? resp : resp?.data || [];
        setVersoes(Array.isArray(data) ? data : []);
      } catch (err) {
        setObjSnackBar({
          type: "error",
          msg: "Erro ao carregar versões da prévia",
        });
      } finally {
        setLoading(false);
      }
    };
    carregarVersoes();
  }, [open, idPrevia, tipoParam]);

  const handleClose = () => {
    // Reset estados ao fechar
    setLoadingCreate(false);
    setObjSnackBar({ type: "", msg: "" });
    onClose && onClose();
  };

  const handleGerarProjeto = async (versaoSelecionada) => {
    if (!versaoSelecionada) {
      setObjSnackBar({ type: "warning", msg: "Selecione uma versão" });
      return;
    }

    setLoadingCreate(true);

    try {
      // Criar projeto e versão em uma única chamada
      const body = {
        idPrevia: Number(idPrevia),
        idPreviaVersao: Number(
          versaoSelecionada?.idPreviaVersao || versaoSelecionada?.id
        ),
      };

      let respProjeto;
      if (tipoParam === "PIS-COFINS") {
        respProjeto = await iniciarProjetoPisCofins(
          body.idPrevia,
          body.idPreviaVersao
        );
      } else if (tipoParam === "ICMS") {
        respProjeto = await iniciarProjetoIcms(
          body.idPrevia,
          body.idPreviaVersao
        );
      }

      if (!respProjeto?.sucesso) {
        throw new Error(respProjeto?.mensagem || "Erro ao criar projeto");
      }

      // O crédito de PIS/COFINS é lançado só pelo backend, na aprovação do projeto.

      // Mostrar sucesso e fechar modal
      setObjSnackBar({
        type: "success",
        msg: "Projeto gerado com sucesso! 🎉",
      });

      handleClose();
      if (typeof rechargeData === "function") rechargeData();
    } catch (error) {
      setObjSnackBar({
        type: "error",
        msg:
          error?.response?.data?.mensagem ||
          error?.message ||
          "Erro ao gerar projeto",
      });
    } finally {
      setLoadingCreate(false);
    }
  };

  return (
    <>
      <Loading loading={loadingCreate} />
      <Dialog
        open={open && !loadingCreate}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        {loading ? (
          <Loading loading={loading} />
        ) : (
          <DialogContent>
            <div className={styles.containerTitle}>
              <div className={styles.title}>
                <FormatListNumberedIcon /> Gerar Projeto - {tipoParam}
              </div>
            </div>

            <div className={styles.containerVersao}>
              {versoes.length === 0 && (
                <div style={{ padding: "0.5rem", color: "#666" }}>
                  Nenhuma versão encontrada para esta prévia.
                </div>
              )}
              {versoes.map((versao, index) => (
                <div key={index} className={styles.itemVersao}>
                  <span>
                    <h4>{versao.descricao || "Versão"}</h4>
                    <p style={{ fontSize: "0.8rem", color: "#A4A4A4" }}>
                      ID: {versao.idPreviaVersao || versao.id}
                      {versao.dtCadastro && (
                        <>
                          {" "}
                          •{" "}
                          {new Date(versao.dtCadastro).toLocaleDateString(
                            "pt-BR"
                          )}
                        </>
                      )}
                    </p>
                  </span>
                  <Button
                    variant="outlined"
                    onClick={() => handleGerarProjeto(versao)}
                    className={styles.btnSelecionar}
                    size="small"
                    disabled={loadingCreate}
                  >
                    Selecionar
                  </Button>
                </div>
              ))}
            </div>

            <div className={styles.containerBtn}>
              <Button
                variant="outlined"
                onClick={handleClose}
                className={styles.btnFechar}
                disabled={loadingCreate}
              >
                Fechar
              </Button>
            </div>
          </DialogContent>
        )}
      </Dialog>

      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
    </>
  );
};

export default ModalVersoesProjeto;
